// Office quotes list. The calculator is a separate full-screen tool at
// /office/quotes/new (the V1 standalone-calculator workflow): this page lists the
// quotes (reference, status, dates, customer and portfolio, tier, value) with a "New Quote"
// button that navigates to the calculator. Each row opens the quote's own page (the Quote
// Hub) and carries ONE action plus a "..." menu (Session ARP). On a successful send the
// calculator returns here and hands back a success summary (shown as a banner with the
// accept link to copy).
import { useCallback, useEffect, useRef, useState } from "react";
import { Plus } from "lucide-react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import type { QuoteListItem, ScrubSummary } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { monthLabel, liftTypeDisplayLabel } from "../../lib/quoteFunnel";
import { quoteStatusLabel, quoteStatusTone } from "../../lib/quoteStatus";
import {
  NO_RECIPIENT_HINT,
  QUOTE_FORCED_DELETE_MESSAGE,
  QUOTE_FORCED_DELETE_WARNING,
  quoteDeleteIsForced,
  quoteResumeState,
  quoteRowActions,
  quoteSendConfirmLabel,
} from "../../lib/quoteActions";
import { useAuth } from "../../lib/auth";
import { formatDate } from "../../lib/format";
import { ConfirmDeleteModal, DELETE_CONFIRM_MESSAGE } from "../../components/ConfirmDeleteModal";
import { ConfirmButton } from "../../components/ConfirmButton";
import { AccountOnStopNote, AccountSymbol } from "../../components/AccountSymbol";
import { OverflowMenu, overflowDangerItemCls, overflowItemCls } from "../../components/OverflowMenu";
import { StatusPill } from "../../components/StatusPill";
import { Loader } from "../../components/Loader";
import { PageHeader } from "../../components/PageHeader";
import { QuotePipelineStrip } from "./QuotePipelineStrip";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { ListToolbar } from "../../components/ListToolbar";
import { useListSearch } from "../../lib/useListSearch";
import { useServerPage } from "../../lib/useServerPage";
import { Pager } from "../../components/Pager";
import { ExportCsvMenu } from "../../components/ExportCsvMenu";
import { useSortableColumn } from "../../lib/useSortableColumn";
import { SortableHeader } from "../../components/SortableHeader";
import { FrequencyConfirmModal } from "./FrequencyConfirmModal";
import { MarkQuoteLostModal, ManageLostReasonsModal } from "./LostReasonModals";
import { QuoteVersionsModal } from "./QuoteVersionsModal";
import { useCustomerFilter, CustomerFilterChip } from "./customerFilter";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { useSingleTimeout } from "../../lib/useSingleTimeout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { voidOutcome } from "../../lib/quoteVoidMessage";

// Session ARP: every status pill on this page (table row and mobile card) is the shared StatusPill at
// the shared quoteStatusTone, the same pill the quote's own page shows, so no quote screen carries a
// colour of its own. The word is always shown, so colour is never the only signal (house rule).

// Session AAV (Loop 1): the status filters, incl. those added for the analytics deep links.
// The funnel-classification filters ("sent", "not_opened", "opened") use the client mirror of
// the server's analytics predicates (src/lib/quoteFunnel.ts, parity-tested), so a clicked
// analytics count and the list it opens always agree. "live" = awaiting the customer
// (pending/sent/opened); "reached_accepted" and "declined" are URL-only values (no chip): the
// funnel's cumulative Accepted stage and the future decline path link with them. Unknown
// values fall back to "all".
type QuoteFilter =
  | "all"
  | "draft"
  | "sent"
  | "live"
  | "not_opened"
  | "opened"
  | "accepted"
  | "reached_accepted"
  | "signed"
  | "declined"
  | "lost"
  | "expired"
  | "voided";
const VALID_FILTERS = new Set<QuoteFilter>([
  "all", "draft", "sent", "live", "not_opened", "opened", "accepted", "reached_accepted", "signed", "declined", "lost", "expired", "voided",
]);
// URL-only filters carry no chip; an active one shows a dismissible banner instead.
const URL_ONLY_FILTERS = new Set<QuoteFilter>(["reached_accepted", "declined"]);
const URL_ONLY_FILTER_LABEL: Record<string, string> = {
  reached_accepted: "Quotes that reached acceptance (accepted or signed)",
  declined: "Quotes the customer declined online",
};
function filterFromParam(raw: string | null): QuoteFilter {
  return raw && VALID_FILTERS.has(raw as QuoteFilter) ? (raw as QuoteFilter) : "all";
}

// The tier deep-link param, validated to the customer-facing keys.
function tierFromParam(raw: string | null): "core" | "care" | "complete" | null {
  return raw === "core" || raw === "care" || raw === "complete" ? raw : null;
}
// The month deep-link param (yyyy-mm), from the analytics monthly trend.
function monthFromParam(raw: string | null): string | null {
  return raw && /^\d{4}-\d{2}$/.test(raw) ? raw : null;
}
// The created-range deep-link params (yyyy-mm-dd, inclusive), carried by every analytics
// segment link so the list matches the range the clicked count was computed over.
function dateFromParam(raw: string | null): string | null {
  return raw && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : null;
}
// Session SC2C: the list is 50 at a time and the SERVER filters, searches and sorts it (GET /api/quotes, quoteListQuery.ts).
const QUOTE_PAGE_SIZE = 50;
const TIER_FILTER_LABEL: Record<string, string> = { core: "Core", care: "Care", complete: "Complete" };

// Session ARP (Q-4): the ONE answer to "which tier does this row show", read by the Tier column, its
// sort and the mobile card's tier pill so the three cannot disagree. The tier the customer ACCEPTED
// once there is one (the server sends acceptedTier only on an accepted or signed quote), else the tier
// the quote recommends. The analytics ?tier= deep link deliberately still filters on recommendedTier,
// because that is what the analytics count it came from counted.
type TierKey = "core" | "care" | "complete";
function shownTier(q: QuoteListItem): { tier: TierKey; accepted: boolean } | null {
  if (q.acceptedTier) return { tier: q.acceptedTier, accepted: true };
  if (q.recommendedTier) return { tier: q.recommendedTier, accepted: false };
  return null;
}
// ...and the price of THAT tier, for the Value column, its sort and the mobile card, so a row never
// names one tier beside another tier's price (the accepted tier's figure once there is one, else the
// recommended tier's, as analyticsQuoteValue already treats a quote's value).
function shownValue(q: QuoteListItem): number | null {
  const own = q.acceptedTier ? (q.acceptedAnnualValue ?? null) : (q.annualValue ?? null);
  // A same-tier renewal record carries no tier prices of its own (its price lives on the successor
  // contract), so it reads the contract's figure rather than a blank. Only a fallback: the quote's own
  // price wins whenever it has one, because a contract's value can later move (a CPI rise).
  return own ?? q.contract?.annualValue ?? null;
}

const BILLING_LABEL: Record<string, string> = {
  monthly: "monthly",
  bi_monthly: "two-monthly",
  quarterly: "quarterly",
  six_monthly: "six-monthly",
  annual: "annual",
};
// "£262/yr (six-monthly)" when the figure came from the contract, so the cadence the customer pays on is visible.
function valueText(q: QuoteListItem): string {
  const v = shownValue(q);
  if (v == null) return "Not priced";
  const own = q.acceptedTier ? q.acceptedAnnualValue : q.annualValue;
  const cadence = own == null && q.contract ? BILLING_LABEL[q.contract.billingFrequency] : undefined;
  return `${gbp(v)}/yr${cadence ? ` (${cadence})` : ""}`;
}

// The Opened column's text. "Not opened" is only true of a quote the customer has not had in front of
// them; a quote that is accepted or signed was agreed another way (a renewal link, an imported record),
// and saying "Not opened" beside SIGNED contradicts the status pill.
function openedText(q: QuoteListItem): { text: string; opened: boolean } {
  if (q.openedAt) return { text: formatDate(q.openedAt), opened: true };
  if (q.status === "signed" || q.status === "accepted") {
    return { text: q.renewalOfContractId ? "Via renewal link" : q.status === "signed" ? "Signed" : "Accepted", opened: true };
  }
  return { text: "Not opened", opened: false };
}

// The one class every row's primary control wears (Session ARP): a small outlined brand-green button,
// sized (px-2, beside the menu at gap-1.5) so "Mark lost" and the "..." trigger share one line in the
// Actions column at 1280px (measured; see the colgroup).
const ROW_PRIMARY_CLS =
  "whitespace-nowrap rounded-ele border border-ele-green px-2 py-1 text-xs text-ele-green transition hover:bg-ele-green hover:text-ele-bg disabled:opacity-50";

// A yyyy-mm-dd range bound from the analytics link, shown as a day ("5 Sep 2026"); an unreadable value shows as typed.
function rangeBoundLabel(iso: string): string {
  const day = formatDate(`${iso}T00:00:00Z`);
  return day === "-" ? iso : day;
}

interface CreatedSummary {
  quoteRef: string;
  token: string | null;
  emailed: boolean;
  email: string;
  recommendedLabel: string;
  annual: number | null;
}

function gbp(n: number): string {
  return `£${Math.round(n).toLocaleString("en-GB")}`;
}

function acceptUrl(token: string): string {
  return `${window.location.origin}/quote/${token}`;
}

// Session ABZ: the site line shown under a quote's reference, so same-day quotes for one customer
// are distinguishable at a glance. Combines the site name and address (either may be missing) and
// truncates the whole line to 60 characters with an ellipsis so the narrow QUOTE column never
// overflows. Returns "" when neither is present (nothing renders).
function quoteSiteLine(q: QuoteListItem): string {
  const name = (q.site?.name ?? q.siteName ?? "").trim();
  const address = (q.siteAddress ?? "").trim();
  const line = [name, address].filter(Boolean).join(" - ");
  return line.length > 60 ? `${line.slice(0, 59).trimEnd()}...` : line;
}

// Session AAQ: the hover text for a quote's manual-price-override badge, surfacing the forced value,
// the office justification and who set it (the list is the office's de facto quote detail).
function overrideTooltip(o: NonNullable<QuoteListItem["override"]>): string {
  const tierLabel = o.tier ? o.tier.charAt(0).toUpperCase() + o.tier.slice(1) : "";
  const parts = [`${gbp(o.value)}/yr forced on ${tierLabel} (calculator bypassed).`];
  if (o.reason) parts.push(`Reason: ${o.reason}.`);
  if (o.setByName) parts.push(`Set by ${o.setByName}${o.setAt ? ` on ${formatDate(o.setAt)}` : ""}.`);
  return parts.join(" ");
}

// The row's action palette: Session AAG gave every row action its own filled colour, up to thirteen
// of them side by side. Session ARP replaced it by the Director's ruling of 25 September 2026: one
// outlined brand-green control plus the shared "..." menu, brand tokens only.

async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the legacy path
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export function QuotesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Session ADI: when linked from the customer detail page with ?customerId=, show only that
  // customer's quotes (composes with the status filter, which defaults to "all").
  const { customerId, clear } = useCustomerFilter();

  // Card list below md, table otherwise -- decided once at mount (Session AHB).
  const [mobileCards] = useState(initialMobileLayout);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const resetCopiedKeyLater = useSingleTimeout();
  // A copy that failed (clipboard blocked): shown briefly inline on the copy button instead of
  // silently doing nothing, so the office knows to copy the accept link by hand.
  const [copyFailedKey, setCopyFailedKey] = useState<string | null>(null);
  const resetCopyFailedKeyLater = useSingleTimeout();
  // Session AAV (Loop 1): the initial filter can arrive in the URL (?filter=signed etc.) so the
  // analytics dashboard's segments deep-link straight to the underlying quotes.
  const [filter, setFilter] = useState<QuoteFilter>(() => filterFromParam(searchParams.get("filter")));
  // Session AAT: when set, only quotes lost for this reason id are shown (a lost quote's reason chip
  // links here). Independent of the status filter above.
  const [reasonFilter, setReasonFilter] = useState<{ id: string; label: string } | null>(null);
  // Session AAV (Loop 1): the analytics tier / lift-type / month / range / reason segments
  // deep-link here too. Each shows a dismissible banner and composes with the status filter.
  const [tierFilter, setTierFilter] = useState<"core" | "care" | "complete" | null>(() => tierFromParam(searchParams.get("tier")));
  const [liftTypeFilter, setLiftTypeFilter] = useState<string | null>(() => searchParams.get("liftType"));
  const [monthFilter, setMonthFilter] = useState<string | null>(() => monthFromParam(searchParams.get("month")));
  // The created-range the analytics figures were computed over, carried on every segment link
  // so the clicked count and this list agree.
  const [rangeFilter, setRangeFilter] = useState<{ from: string | null; to: string | null } | null>(() => {
    const from = dateFromParam(searchParams.get("from"));
    const to = dateFromParam(searchParams.get("to"));
    return from || to ? { from, to } : null;
  });
  // A loss-reason deep link (?reason=<id>): reuses the same reasonFilter the in-row chips set;
  // the label resolves from the loaded rows once they arrive (see the effect below).
  const [reasonIdParam, setReasonIdParam] = useState<string | null>(() => searchParams.get("reason"));
  // Session AEH B2: a ?q=<ref> deep link (the dashboard's Active Quotes panel) surfaces exactly that
  // quote on the hub, overriding the status filter so a draft/pending row is never hidden.
  const [refFilter, setRefFilter] = useState<string | null>(() => searchParams.get("q"));
  // Q-3, Session AQT: free-text search, URL-bound under its own "search" param -- deliberately NOT
  // "q", which ?q=<ref> above already owns for the exact-reference deep link; the two coexist on
  // the URL at once without colliding.
  const { search, setSearch } = useListSearch("search");
  const sort = useSortableColumn("quotes");

  // Re-sync from the URL when it changes while mounted (a second analytics link, browser
  // back/forward). One-way: chip clicks update state only, never the URL.
  useEffect(() => {
    setFilter(filterFromParam(searchParams.get("filter")));
    setTierFilter(tierFromParam(searchParams.get("tier")));
    setLiftTypeFilter(searchParams.get("liftType"));
    setMonthFilter(monthFromParam(searchParams.get("month")));
    const from = dateFromParam(searchParams.get("from"));
    const to = dateFromParam(searchParams.get("to"));
    setRangeFilter(from || to ? { from, to } : null);
    setReasonIdParam(searchParams.get("reason"));
    setRefFilter(searchParams.get("q"));
  }, [searchParams]);
  // Per-row action (send a draft / void a sent envelope) in flight, and its result banner.
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  // Session ARP: WHICH action is in flight on that row. actionBusy still disables every control on the
  // row; this decides only which control says so ("Resending...", "Duplicating..."). With one visible
  // button and the rest in a menu, keying every busy LABEL on the row alone made the button announce
  // "Resending..." while a Duplicate from the menu ran. The same reason voidingId exists, below.
  const [busyKind, setBusyKind] = useState<"edit" | "resend" | "send" | "void" | "reopen" | "duplicate" | "extend" | null>(null);
  function startRowAction(id: string, kind: NonNullable<typeof busyKind>) {
    setActionBusy(id);
    setBusyKind(kind);
  }
  // Session AAT: the quote being marked lost (opens the reason picker), and the manage-reasons modal.
  const [lostTarget, setLostTarget] = useState<{ id: string; ref: string } | null>(null);
  const [manageReasonsOpen, setManageReasonsOpen] = useState(false);
  // Session AAT: the quote whose version history is open.
  const [historyTarget, setHistoryTarget] = useState<{ id: string; ref: string } | null>(null);

  const [created, setCreated] = useState<CreatedSummary | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<{ id: string; ref: string; signed: boolean } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Session AIE: the row-action result banner now carries a KIND, and the reason is a live incident.
  // A draft quote with no recipient was refused twice by the server (400, both logged), and the
  // office saw nothing: the refusal was rendered in the SAME neutral purple role="status" box, in the
  // same body text, as "sent to the customer", pinned above a 36-row table well outside the viewport
  // of the row whose Send button had just been pressed. The message was produced correctly and was
  // simply invisible. So an error now renders as role="alert" in the danger colour and scrolls itself
  // into view, matching the canonical house pattern (ProjectDetailPage's { text, kind } notice).
  // Every row action on this page shared the flaw; Send is only where it bit.
  const [deleteResult, setDeleteResult] = useState<{ text: string; kind: "ok" | "error" } | null>(null);
  const resultRef = useRef<HTMLDivElement | null>(null);
  const actionOk = useCallback((text: string) => setDeleteResult({ text, kind: "ok" }), []);
  const actionFailed = useCallback((text: string) => setDeleteResult({ text, kind: "error" }), []);
  // Only a failure steals the scroll position. A success is confirmation of something the office
  // just chose to do, and yanking the page on every successful row action would be its own defect.
  useEffect(() => {
    // scrollIntoView is OPTIONAL-CALLED, not just optional-chained on the ref. jsdom does not
    // implement it, and an unguarded call throws inside this passive effect, which unmounts the
    // whole page: the red alert this effect exists to reveal, and the quotes table with it. The
    // house pattern everywhere else in this repo already guards the METHOD (ProjectDetailPage,
    // KnowledgePage, FaultAssistant), and three of those say why in a comment.
    if (deleteResult?.kind === "error") resultRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [deleteResult]);

  // "Resent" transient per-row confirmation (mirrors the Copy link "Copied" pattern).
  const [resentKey, setResentKey] = useState<string | null>(null);
  const resetResentKeyLater = useSingleTimeout();

  // Session AAO: the quote whose requested frequency change the office is reviewing (opens the
  // confirm-and-send-ESA panel), or null when the panel is closed.
  const [freqReviewTarget, setFreqReviewTarget] = useState<{ id: string; ref: string } | null>(null);

  // Session SC2C: the status filter, the analytics deep-link filters, the customer link, the search and the sort are all asked of the SERVER
  // (GET /api/quotes, quoteListQuery.ts), which answers one page of 50 and the TRUE total of every match. The search is the one in the address
  // bar (the toolbar writes it there after a short pause). A ?q=<ref> deep link is the exact-reference override and keeps its own parameter name.
  const urlSearch = (searchParams.get("search") ?? "").trim();
  const queryKey = JSON.stringify({ filter, refFilter, customerId, reasonId: reasonFilter?.id ?? null, tierFilter, liftTypeFilter, monthFilter, rangeFilter, urlSearch, sk: sort.sortKey, sd: sort.sortDir });
  const page = useServerPage(queryKey, (at) =>
    api.quotes.listPage({
      ...(refFilter ? { ref: refFilter } : {}),
      ...(customerId ? { customerId } : {}),
      ...(reasonFilter ? { reason: reasonFilter.id } : {}),
      ...(tierFilter ? { tier: tierFilter } : {}),
      ...(liftTypeFilter ? { liftType: liftTypeFilter } : {}),
      ...(monthFilter ? { month: monthFilter } : {}),
      ...(rangeFilter?.from ? { from: rangeFilter.from } : {}),
      ...(rangeFilter?.to ? { to: rangeFilter.to } : {}),
      ...(filter !== "all" ? { filter } : {}),
      ...(urlSearch ? { q: urlSearch } : {}),
      ...(sort.sortKey ? { sort: sort.sortKey, dir: sort.sortDir } : {}),
      limit: QUOTE_PAGE_SIZE,
      offset: at,
    }),
  );
  const loading = page.loading;
  const listError = page.error;
  const filtered: QuoteListItem[] = page.data?.items ?? [];
  const total = page.data?.total ?? 0;
  const load = () => page.reload();
  const anyFilter = filter !== "all" || Boolean(refFilter || customerId || reasonFilter || tierFilter || liftTypeFilter || monthFilter || rangeFilter);

  // Resolve a ?reason=<id> deep link (an analytics loss-reason row) into the same reasonFilter the in-row chips set. The server names the
  // reason in its answer (reasonLabel); a stale/unknown id still filters (matching nothing) with a generic label.
  const reasonLabelFromServer = page.data?.reasonLabel ?? null;
  useEffect(() => {
    if (!reasonIdParam) return;
    setReasonFilter({ id: reasonIdParam, label: reasonLabelFromServer ?? "the selected reason" });
  }, [reasonIdParam, reasonLabelFromServer]);

  // The calculator returns here with a created-quote summary in the router state.
  // Show it once, then clear the state so a refresh does not re-show it.
  useEffect(() => {
    const state = location.state as { created?: CreatedSummary } | null;
    if (state?.created) {
      setCreated(state.created);
      navigate(location.pathname, { replace: true, state: null });
    }
    // run once for the incoming navigation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function copyLink(key: string, token: string | null) {
    if (!token) return;
    const ok = await copyText(acceptUrl(token));
    if (ok) {
      setCopyFailedKey((k) => (k === key ? null : k));
      setCopiedKey(key);
      resetCopiedKeyLater(() => setCopiedKey((k) => (k === key ? null : k)), 2000);
    } else {
      // Clipboard blocked (permissions, an insecure context, an older browser): surface a brief
      // inline failure rather than swallowing it, so the office does not think the link was copied.
      setCopiedKey((k) => (k === key ? null : k));
      setCopyFailedKey(key);
      resetCopyFailedKeyLater(() => setCopyFailedKey((k) => (k === key ? null : k)), 3000);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const summary: ScrubSummary = await api.quotes.delete(deleteTarget.id, deleteTarget.signed);
      void page.reload();
      actionOk(`Quote ${deleteTarget.ref} deleted.${summary.notes.length ? ` ${summary.notes.join(" ")}` : ""}`);
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError(friendlyApiError(err, "Could not delete the quote. Please try again."));
    } finally {
      setDeleting(false);
    }
  }

  // Edit a quote in the calculator, pre-filled from its stored form state (Resume a draft, or Edit
  // a not-yet-accepted sent quote). The calculator revises it IN PLACE on save (same ref); the ref
  // is passed so the calculator can show an "Editing quote [ref]" banner (Session AAG).
  async function editQuote(id: string) {
    startRowAction(id, "edit");
    try {
      // Live, never the request cache (Session ARU), exactly as the quote's own page does: this is the
      // state the calculator opens on, and the calculator's own re-fetch is live too.
      const q = await api.quotes.get(id, { noCache: true });
      // The router state is built by the shared quoteResumeState (Session ARP), the same object the
      // quote's own page hands the calculator: status rides along (Session AAV) so the calculator
      // autosaves a DRAFT resume but never an already-sent quote; the stored payload rides along
      // (Session AHP) so it can rebuild a quote with no calculatorState (one made by the MCP
      // create_quote tool); portfolioId rides along (Session AIX) so its live pricing preview
      // resolves the quote's real portfolio band while editing.
      navigate("/office/quotes/new", { state: quoteResumeState(q) });
    } catch {
      actionFailed("Could not open that quote. Please try again.");
      setActionBusy(null);
    }
  }

  // Resend a sent (not-yet-accepted) quote: re-email the same accept link, reset it to pending and
  // clear the Opened timestamp. No new record, same quote ref. Shows a brief "Resent" on the row.
  async function resendQuote(id: string, ref: string) {
    startRowAction(id, "resend");
    try {
      // Session AIE: the response is READ, not discarded. The route answers 200 with `emailed:
      // false` when it updated the quote but Gmail did not accept the message (it is not connected,
      // or the send threw), because the status write deliberately precedes the email. This handler
      // used to throw that flag away and flash "Resent" regardless, so the office was given a
      // positive confirmation for a quote the customer never received. Send already branched on it;
      // Resend did not, and the asymmetry was the whole defect.
      const res = await api.quotes.resend(id);
      await load();
      if (!res.emailed) {
        actionFailed(
          `Quote ${ref} was updated but the email did not go out. Check that Gmail is connected, then resend it.`,
        );
        return;
      }
      // Session ARP: said in the result banner too, as Send says it. The reload above swaps the table for
      // its loading state, which closes the row's menu, so a Resend pressed IN the menu (a quote whose
      // held frequency change owns the row's button) would otherwise confirm into a menu that is gone.
      actionOk(`Quote ${ref} resent to ${res.recipient ?? "the customer"}.`);
      setResentKey(id);
      resetResentKeyLater(() => setResentKey((k) => (k === id ? null : k)), 2000);
    } catch (err) {
      actionFailed(friendlyApiError(err, `Could not resend quote ${ref}. Please try again.`));
    } finally {
      setActionBusy(null);
    }
  }

  // Send a draft: flip it to pending and email the customer the accept link.
  async function sendDraft(id: string, ref: string) {
    startRowAction(id, "send");
    try {
      const res = await api.quotes.send(id);
      await load();
      // Session AIE: an unsent email is a FAILURE, not a status. The quote is now marked sent while
      // the customer has nothing, which is the state the office most needs to act on, so it is
      // coloured and announced as an error rather than sharing the success banner.
      if (res.emailed) actionOk(`Quote ${ref} sent to ${res.recipient ?? "the customer"}.`);
      else actionFailed(`Quote ${ref} is marked as sent but the email did not go out. Check that Gmail is connected, then resend it.`);
    } catch (err) {
      actionFailed(friendlyApiError(err, `Could not send quote ${ref}. Please try again.`));
    } finally {
      setActionBusy(null);
    }
  }

  // Void a sent-but-unsigned quote (cancel its Signable envelope + draft contract) so the
  // office can correct it without cancelling the envelope in Signable by hand. Confirmed via
  // the shared two-step ConfirmButton (Session AAV; native window.confirm retired, matching
  // the platform two-step pattern). voidingId drives ONLY the "Voiding..." label, so another
  // in-flight row action never falsely announces a void.
  const [voidingId, setVoidingId] = useState<string | null>(null);
  async function voidQuote(id: string, ref: string) {
    // Its own kind too (Session ARP), so no OTHER control's label announces itself during a void.
    startRowAction(id, "void");
    setVoidingId(id);
    try {
      const res = await api.quotes.voidEnvelope(id);
      await load();
      const outcome = voidOutcome(`Quote ${ref}`, res);
      if (outcome.kind === "error") actionFailed(outcome.text);
      else actionOk(outcome.text);
    } catch (err) {
      actionFailed(friendlyApiError(err, `Could not void quote ${ref}. Please try again.`));
    } finally {
      setActionBusy(null);
      setVoidingId(null);
    }
  }

  // Reopen a lost or voided quote: returns it to an editable draft, keeping its ref and accept
  // token, so the office can revise and re-send it in place (a voided quote is no longer a dead end
  // that forces a Duplicate to a fresh number). The server clears the loss capture / stale envelope.
  async function reopenQuote(id: string, ref: string) {
    startRowAction(id, "reopen");
    try {
      await api.quotes.reopen(id);
      await load();
      actionOk(`Quote ${ref} reopened as a draft.`);
    } catch (err) {
      actionFailed(friendlyApiError(err, `Could not reopen quote ${ref}. Please try again.`));
    } finally {
      setActionBusy(null);
    }
  }

  // Duplicate a quote into an independent DRAFT (fresh ref, no history), then reload so it appears.
  async function duplicateQuote(id: string, ref: string) {
    startRowAction(id, "duplicate");
    try {
      const dup = await api.quotes.duplicate(id);
      await load();
      actionOk(`Quote ${ref} duplicated as draft ${dup.quoteRef}.`);
    } catch (err) {
      actionFailed(friendlyApiError(err, `Could not duplicate quote ${ref}. Please try again.`));
    } finally {
      setActionBusy(null);
    }
  }

  // Extend a quote's validity: push validUntil forward (reviving an expired quote to sent) so the
  // same accept link stays live (Session AAT).
  async function extendValidity(id: string, ref: string) {
    startRowAction(id, "extend");
    try {
      await api.quotes.extendValidity(id);
      await load();
      actionOk(`Quote ${ref} validity extended.`);
    } catch (err) {
      actionFailed(friendlyApiError(err, `Could not extend quote ${ref}. Please try again.`));
    } finally {
      setActionBusy(null);
    }
  }

  // Session ARP: a row's actions, by the Director's ruling of 25 September 2026. ONE control for the
  // action that matters at the quote's status, then the shared "..." menu holding every other action
  // the quote offers. quoteRowActions (src/lib/quoteActions.ts) decides both from the rules the quote's
  // own page reads, so a row cannot offer an action the page does not, and nothing is dropped: the
  // primary plus the menu is the quote's whole set. Every control calls the handler it always called,
  // with the same arguments (quoteActionParity.test.tsx compares the requests against the quote page).
  function renderRowActions(q: QuoteListItem) {
    const { primary, menu } = quoteRowActions(q, { isAdmin });
    const inMenu = (a: (typeof menu)[number]) => menu.includes(a);
    // busy disables every control on the row; busyAs names the action whose label says it is running.
    const busy = actionBusy === q.id;
    const busyAs = (kind: NonNullable<typeof busyKind>) => busy && busyKind === kind;

    // Send and Resend are two-step (Session AEH A2), so a single click never fires the email, and
    // the armed label NAMES THE ADDRESS the server will use (Session AIE; the branded-confirm rule).
    // A quote with no resolvable recipient carries the hint on hover but stays LIVE, deliberately
    // (Session AIE adversarial review): a disabled control paints no title and leaves the tab order,
    // so the office would get a greyed button and no reason, where pressing it returns an alert
    // naming both places to fix. Usually the row's primary; in the menu only when a held frequency
    // change outranks it (a renewal quotation carries that marker while it awaits the customer).
    const sendControl = (className: string) => (
      <ConfirmButton
        label="Send"
        confirmLabel={quoteSendConfirmLabel("Send", q)}
        busy={busyAs("send")}
        busyLabel="Sending..."
        disabled={busy}
        title={q.recipientEmail === null ? NO_RECIPIENT_HINT : undefined}
        onConfirm={() => void sendDraft(q.id, q.quoteRef)}
        className={className}
        armedClassName="!font-bold"
        armedNote={<AccountOnStopNote customerId={q.customer?.id} />}
      />
    );
    const resendControl = (className: string) => (
      <ConfirmButton
        label={resentKey === q.id ? "Resent" : "Resend"}
        confirmLabel={quoteSendConfirmLabel("Resend", q)}
        busy={busyAs("resend")}
        busyLabel="Resending..."
        disabled={busy}
        title={q.recipientEmail === null ? NO_RECIPIENT_HINT : undefined}
        onConfirm={() => void resendQuote(q.id, q.quoteRef)}
        className={className}
        armedClassName="!font-bold"
        armedNote={<AccountOnStopNote customerId={q.customer?.id} />}
      />
    );
    // Session AAO: an accepted quote with a pending frequency change opens the confirm-and-send-ESA
    // gate. Session AIK: a RENEWAL shares the marker but NOT the gate (that panel reprices through the
    // engine and sends the ESA, and a renewal must do neither), so its control goes to the renewed
    // contract's page, where the decision lives.
    const reviewFrequencyControl = (className: string) =>
      q.renewalOfContractId ? (
        <Link to={`/office/contracts/${q.renewalOfContractId}`} className={className}>
          Review frequency
        </Link>
      ) : (
        <button type="button" onClick={() => setFreqReviewTarget({ id: q.id, ref: q.quoteRef })} className={className}>
          Review frequency
        </button>
      );
    const extendControl = (className: string) => (
      // Session AAT: push the expiry forward, reviving an expired quote to sent so the same accept
      // link stays live.
      <button type="button" disabled={busy} onClick={() => extendValidity(q.id, q.quoteRef)} className={className}>
        {busyAs("extend") ? "Extending..." : "Extend"}
      </button>
    );
    const reopenControl = (className: string) => (
      // A lost or voided quote back to an editable draft, keeping its ref and accept token (Session
      // AAW extends this to voided).
      <button type="button" disabled={busy} onClick={() => reopenQuote(q.id, q.quoteRef)} className={className}>
        {busyAs("reopen") ? "Reopening..." : "Reopen"}
      </button>
    );
    const markLostControl = (className: string) => (
      // Session AAT: record the deal as lost, with a reason.
      <button type="button" disabled={busy} onClick={() => setLostTarget({ id: q.id, ref: q.quoteRef })} className={className}>
        Mark lost
      </button>
    );

    return (
      <div className="flex flex-wrap items-center justify-end gap-1.5">
        {primary === "send" && sendControl(ROW_PRIMARY_CLS)}
        {primary === "resend" && resendControl(ROW_PRIMARY_CLS)}
        {primary === "extend" && extendControl(ROW_PRIMARY_CLS)}
        {primary === "reopen" && reopenControl(ROW_PRIMARY_CLS)}
        {primary === "markLost" && markLostControl(ROW_PRIMARY_CLS)}
        {primary === "reviewFrequency" && reviewFrequencyControl(ROW_PRIMARY_CLS)}
        <OverflowMenu label={`Actions for ${q.quoteRef}`}>
          {/* The quote's own page, first: a keyboard user cannot click a table row. */}
          <Link to={`/office/quotes/${encodeURIComponent(q.id)}`} className={overflowItemCls}>
            Open quote
          </Link>
          {inMenu("send") && sendControl(overflowItemCls)}
          {inMenu("resend") && resendControl(overflowItemCls)}
          {/* Edit revises IN PLACE on save. Never offered on a RENEWAL quotation (Session APW): the
              calculator knows nothing of a renewal, and its save refuses one. */}
          {inMenu("edit") && (
            <button type="button" disabled={busy} onClick={() => editQuote(q.id)} className={overflowItemCls}>
              Edit
            </button>
          )}
          {/* Re-quote (Session AAT): an expired quote back into the calculator; the same Edit flow. */}
          {inMenu("requote") && (
            <button type="button" disabled={busy} onClick={() => editQuote(q.id)} className={overflowItemCls}>
              Re-quote
            </button>
          )}
          {/* A draft has no accept token yet, so it previews the office quote PDF, rendered on demand
              from the saved draft payload (GET /api/quotes/:id/pdf). */}
          {inMenu("viewPdf") && (
            <a href={`/api/quotes/${q.id}/pdf`} target="_blank" rel="noopener noreferrer" className={overflowItemCls}>
              View PDF
            </a>
          )}
          {/* The customer-facing interactive quote page, in a new tab. */}
          {inMenu("viewQuote") && q.token && (
            <a href={acceptUrl(q.token)} target="_blank" rel="noopener noreferrer" className={overflowItemCls}>
              View quote
            </a>
          )}
          {inMenu("copyLink") && q.token && (
            <button type="button" onClick={() => copyLink(q.id, q.token)} className={overflowItemCls}>
              {copiedKey === q.id ? "Copied" : copyFailedKey === q.id ? "Could not copy the link" : "Copy link"}
            </button>
          )}
          {/* The append-only version snapshots (Session AAT). Offered at every status: a reopened
              draft carries the version Session APW's reopen wrote. */}
          {inMenu("history") && (
            <button type="button" onClick={() => setHistoryTarget({ id: q.id, ref: q.quoteRef })} className={overflowItemCls}>
              History
            </button>
          )}
          {/* An independent DRAFT copy with a fresh ref (Session AAT). */}
          {inMenu("duplicate") && (
            <button type="button" disabled={busy} onClick={() => duplicateQuote(q.id, q.quoteRef)} className={overflowItemCls}>
              {busyAs("duplicate") ? "Duplicating..." : "Duplicate"}
            </button>
          )}
          {inMenu("reviewFrequency") && reviewFrequencyControl(overflowItemCls)}
          {inMenu("extend") && extendControl(overflowItemCls)}
          {inMenu("reopen") && reopenControl(overflowItemCls)}
          {inMenu("markLost") && markLostControl(overflowItemCls)}
          {/* Cancel the Signable envelope and draft contract of a sent-but-unsigned quote, so the
              office can correct it without using Signable by hand. Two-step (Session AAV): the first
              click arms, the second voids. voidingId drives ONLY the "Voiding..." label, so another
              in-flight row action never falsely announces a void. */}
          {inMenu("void") && (
            <ConfirmButton
              label="Void"
              confirmLabel="Confirm void?"
              busy={voidingId === q.id}
              busyLabel="Voiding..."
              disabled={busy}
              onConfirm={() => void voidQuote(q.id, q.quoteRef)}
              className={overflowDangerItemCls}
              armedClassName="!font-bold"
              title="Cancels the signature request and any draft contract. It cannot be undone."
            />
          )}
          {/* Admin only (quoteRowActions holds it for an admin alone). An accepted quote has rendered
              a contract too, so it takes the forced delete, which voids the contract and its Xero draft. */}
          {inMenu("delete") && (
            <button
              type="button"
              onClick={() => {
                setDeleteError(null);
                setDeleteTarget({ id: q.id, ref: q.quoteRef, signed: quoteDeleteIsForced(q.status) });
              }}
              className={overflowDangerItemCls}
            >
              Delete
            </button>
          )}
        </OverflowMenu>
      </div>
    );
  }

  // "All sent" (not "Sent") so the chip is never confused with the narrower `sent` status
  // word on the row chips beside it.
  const FILTERS: { value: typeof filter; label: string }[] = [
    { value: "all", label: "All" },
    { value: "draft", label: "Draft" },
    { value: "sent", label: "All sent" },
    { value: "live", label: "Awaiting customer" },
    { value: "not_opened", label: "Not opened" },
    { value: "opened", label: "Opened" },
    { value: "accepted", label: "Accepted" },
    { value: "signed", label: "Signed" },
    { value: "lost", label: "Lost" },
    { value: "expired", label: "Expired" },
    { value: "voided", label: "Voided" },
  ];

  return (
    <div>
      <PageHeader
        title="Quotes"
        subtitle="Price a new quote in the calculator, then send the customer their accept link."
        className="mb-4"
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={() => setManageReasonsOpen(true)}>
              Lost reasons
            </Button>
            <Button size="sm" icon={<Plus aria-hidden size={15} />} onClick={() => navigate("/office/quotes/new")}>
              New quote
            </Button>
            {/* Session EXP1: the CSV of this list as it is showing (every filter, the search and the sort), in the More actions menu. */}
            <ExportCsvMenu
              list="quotes"
              params={{
                ...(refFilter ? { ref: refFilter } : {}),
                ...(customerId ? { customerId } : {}),
                ...(reasonFilter ? { reason: reasonFilter.id } : {}),
                ...(tierFilter ? { tier: tierFilter } : {}),
                ...(liftTypeFilter ? { liftType: liftTypeFilter } : {}),
                ...(monthFilter ? { month: monthFilter } : {}),
                ...(rangeFilter?.from ? { from: rangeFilter.from } : {}),
                ...(rangeFilter?.to ? { to: rangeFilter.to } : {}),
                ...(filter !== "all" ? { filter } : {}),
                ...(urlSearch ? { q: urlSearch } : {}),
                ...(sort.sortKey ? { sort: sort.sortKey, dir: sort.sortDir } : {}),
              }}
            />
          </>
        }
      />

      {/* Session ASH (Q-7): every quote, project quotation and repair estimate, by stage. */}
      <QuotePipelineStrip />

      {reasonFilter && (
        <div className="mb-3 flex items-center gap-2 text-sm text-ele-body">
          <span>
            Showing quotes lost for: <span className="text-ele-danger">{reasonFilter.label}</span>
          </span>
          <button type="button" onClick={() => setReasonFilter(null)} className="text-xs text-ele-blue underline hover:text-ele-strong">
            Clear
          </button>
        </div>
      )}

      {/* Session AAV (Loop 1): the analytics deep-link filters, each dismissible. */}
      {URL_ONLY_FILTERS.has(filter) && (
        <div className="mb-3 flex items-center gap-2 text-sm text-ele-body">
          <span>
            Showing: <span className="text-ele-strong">{URL_ONLY_FILTER_LABEL[filter]}</span>
          </span>
          <button type="button" onClick={() => setFilter("all")} className="text-xs text-ele-blue underline hover:text-ele-strong">
            Clear
          </button>
        </div>
      )}
      {tierFilter && (
        <div className="mb-3 flex items-center gap-2 text-sm text-ele-body">
          <span>
            Recommended tier: <span className="text-ele-green">{TIER_FILTER_LABEL[tierFilter]}</span>
          </span>
          <button type="button" onClick={() => setTierFilter(null)} className="text-xs text-ele-blue underline hover:text-ele-strong">
            Clear
          </button>
        </div>
      )}
      {liftTypeFilter && (
        <div className="mb-3 flex items-center gap-2 text-sm text-ele-body">
          <span>
            Lift type: <span className="text-ele-blue">{liftTypeDisplayLabel(liftTypeFilter)}</span>
          </span>
          <button type="button" onClick={() => setLiftTypeFilter(null)} className="text-xs text-ele-blue underline hover:text-ele-strong">
            Clear
          </button>
        </div>
      )}
      {monthFilter && (
        <div className="mb-3 flex items-center gap-2 text-sm text-ele-body">
          <span>
            Created in: <span className="text-ele-strong">{monthLabel(monthFilter)}</span>
          </span>
          <button type="button" onClick={() => setMonthFilter(null)} className="text-xs text-ele-blue underline hover:text-ele-strong">
            Clear
          </button>
        </div>
      )}
      {rangeFilter && (
        <div className="mb-3 flex items-center gap-2 text-sm text-ele-body">
          <span>
            Created{rangeFilter.from ? ` from ${rangeBoundLabel(rangeFilter.from)}` : ""}
            {rangeFilter.to ? ` to ${rangeBoundLabel(rangeFilter.to)}` : ""} (the analytics range you clicked)
          </span>
          <button type="button" onClick={() => setRangeFilter(null)} className="text-xs text-ele-blue underline hover:text-ele-strong">
            Clear
          </button>
        </div>
      )}
      {refFilter && (
        <div className="mb-3 flex items-center gap-2 text-sm text-ele-body">
          <span>
            Showing quote: <span className="text-ele-strong">{refFilter}</span>
          </span>
          <button type="button" onClick={() => setRefFilter(null)} className="text-xs text-ele-blue underline hover:text-ele-strong">
            Show all
          </button>
        </div>
      )}

      {deleteResult && (
        <div
          ref={resultRef}
          role={deleteResult.kind === "error" ? "alert" : "status"}
          className={`mb-6 flex items-start justify-between gap-3 rounded-ele border bg-ele-surface p-4 ${
            deleteResult.kind === "error" ? "border-ele-danger" : "border-ele-purple-accent"
          }`}
        >
          <p className={`text-sm ${deleteResult.kind === "error" ? "text-ele-danger" : "text-ele-body"}`}>{deleteResult.text}</p>
          <button type="button" onClick={() => setDeleteResult(null)} className="text-xs text-ele-body underline hover:text-ele-strong">
            Dismiss
          </button>
        </div>
      )}

      {created && (
        <div role="status" className="mb-6 rounded-ele border border-ele-green bg-ele-surface p-5">
          <h2 className="font-heading text-lg text-ele-green">
            Quote {created.quoteRef} created
            {created.annual != null ? `: ${created.recommendedLabel} at ${gbp(created.annual)}/yr ex VAT` : ""}
          </h2>
          {created.emailed && created.email ? (
            <p className="mt-1 text-sm text-ele-green">Sent to {created.email}. The link below is for your reference.</p>
          ) : created.email ? (
            <p className="mt-1 text-sm text-ele-body">The email could not be sent automatically. Send the customer this link instead.</p>
          ) : null}
          {created.token ? (
            <>
              <p className="mt-1 text-sm text-ele-body">Customer accept link:</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <code className="break-all rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong">
                  {acceptUrl(created.token)}
                </code>
                <Button onClick={() => copyLink("created", created.token)}>
                  {copiedKey === "created" ? "Copied" : copyFailedKey === "created" ? "Could not copy the link" : "Copy link"}
                </Button>
              </div>
            </>
          ) : (
            <p className="mt-1 text-sm text-ele-body">This quote has no accept link.</p>
          )}
          <button type="button" onClick={() => setCreated(null)} className="mt-3 text-xs text-ele-body underline hover:text-ele-strong">
            Dismiss
          </button>
        </div>
      )}

      {!loading && !listError && (total > 0 || anyFilter || urlSearch !== "") && (
        <ListToolbar
          className="mb-3"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search ref, customer, site, address or postcode..."
          searchLabel="Search quotes"
          sort={{
            state: sort,
            onChange: (st) => sort.setSort(st.sortKey, st.sortDir),
            alwaysVisible: true,
            options: [
              { key: "created", label: "Created" },
              { key: "site", label: "Site" },
              { key: "customer", label: "Customer" },
              { key: "ref", label: "Quote ref" },
              { key: "status", label: "Status" },
              { key: "validUntil", label: "Valid until" },
              { key: "opened", label: "Opened" },
              { key: "tier", label: "Tier" },
              { key: "value", label: "Value" },
            ],
          }}
          filters={FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setFilter(f.value)}
              aria-pressed={filter === f.value}
              className={`rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
                filter === f.value ? "border-ele-green text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-strong"
              }`}
            >
              {f.label}
            </button>
          ))}
        />
      )}

      {customerId && (
        <div className="mb-3">
          <CustomerFilterChip noun="quotes" onClear={clear} />
        </div>
      )}

      {loading ? (
        <Loader label="Loading the quotes..." />
      ) : listError ? (
        <div role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center">
          <p className="text-ele-strong">Could not load the quotes.</p>
          <button
            type="button"
            onClick={() => void load()}
            className="mt-3 rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-blue transition hover:text-ele-strong"
          >
            Retry
          </button>
        </div>
      ) : total === 0 && !anyFilter && urlSearch === "" ? (
        <EmptyState
          message="No quotes yet"
          hint="Price the first one in the calculator and send the customer their accept link."
          action={
            <Button size="sm" icon={<Plus aria-hidden size={15} />} onClick={() => navigate("/office/quotes/new")}>
              New quote
            </Button>
          }
        />
      ) : filtered.length === 0 ? (
        <EmptyState message={urlSearch ? `No quotes match "${urlSearch}".` : "No quotes match this filter."} />
      ) : mobileCards ? (
        /* Session AHB. Session AFT widened the Status column to 18% after MEASURING the
           widest pill ("Awaiting customer", 143.8px) against the desktop table, and recorded
           in its own handoff that below md the pill still truncates. It did, along with the
           quote reference (80px of content) and the customer (67px). The card gives each its
           own line. The row-level actions stay desktop-only, as they already were: the whole
           card taps through to the quote's own page (Session AQW), which carries every action. */
        <MobileCardList label="Quotes">
          {filtered.map((q) => {
            const siteLine = quoteSiteLine(q);
            const customer = q.customer?.name ?? q.customerName ?? "None";
            // Session ARP: the same tier the desktop Tier column shows (the accepted one once there is one).
            const tier = shownTier(q);
            // The qualifiers the status cell stacks under its pill. On a card they sit on
            // their own full-width row: the lost reason is a FILTER BUTTON, which cannot be
            // nested inside the card's tap-through anchor.
            const qualifiers: React.ReactNode[] = [];
            if (q.renewalOfContractId) {
              qualifiers.push(
                <StatusPill key="renewal" word="Renewal" tone="grey" title="This quote renews an existing contract" />,
              );
            }
            if (q.validUntil && (q.status === "pending" || q.status === "sent" || q.status === "expired")) {
              qualifiers.push(
                <span key="valid" className={`font-menu text-[0.65rem] uppercase tracking-wide ${q.status === "expired" ? "text-ele-danger" : "text-ele-body opacity-70"}`}>
                  {q.status === "expired" ? "Expired" : "Valid to"} {formatDate(q.validUntil)}
                </span>,
              );
            }
            if (q.frequencyChangePending) {
              qualifiers.push(
                <span key="freq" className="font-menu text-[0.65rem] uppercase tracking-wide text-amber-400">
                  Frequency change
                </span>,
              );
            }
            if (q.override) {
              qualifiers.push(
                <span key="ovr" className="font-menu text-[0.65rem] uppercase tracking-wide text-amber-400" title={overrideTooltip(q.override)}>
                  Manual price {gbp(q.override.value)}
                </span>,
              );
            }
            if (q.status === "lost" && q.lostReason) {
              qualifiers.push(
                <button
                  key="lost"
                  type="button"
                  onClick={() => setReasonFilter({ id: q.lostReason!.id, label: q.lostReason!.label })}
                  title={q.lostNote ?? "Filter by this reason"}
                  className="min-h-11 text-left font-menu text-[0.65rem] tracking-wide text-ele-danger underline decoration-dotted hover:text-ele-strong"
                >
                  {q.lostReason.label}
                </button>,
              );
            }
            return (
              <MobileListCard
                key={q.id}
                to={`/office/quotes/${encodeURIComponent(q.id)}`}
                primary={<span className="font-heading">{q.quoteRef}</span>}
                secondary={
                  <>
                    {/* Session SCR1 (SC9B follow-up): the account symbol on the phone card too, plain because the whole card is a link. */}
                    {q.customer?.id && <AccountSymbol plain customerId={q.customer.id} className="!ml-0" />}
                    {siteLine ? `${customer} | ${siteLine}` : customer}
                  </>
                }
                // Whether the customer has OPENED the quote is the most actionable fact on a
                // sales list (it is what says whether to chase), and it was a desktop-only
                // column. Both branches read exactly as the desktop cell does.
                meta={`Created ${formatDate(q.createdAt)} | ${q.openedAt ? `Opened ${formatDate(q.openedAt)}` : openedText(q).text}`}
                right={
                  <>
                    <span className="font-semibold text-ele-strong">{valueText(q)}</span>
                    <StatusPill word={quoteStatusLabel(q.status)} tone={quoteStatusTone(q.status)} />
                    {tier && (
                      // Said in the pill, not only in a title a touch screen never shows.
                      <StatusPill
                        word={`${TIER_FILTER_LABEL[tier.tier]}${tier.accepted ? " · accepted" : ""}`}
                        tone="purple"
                        title={tier.accepted ? "Accepted tier" : "Recommended tier"}
                      />
                    )}
                  </>
                }
                details={qualifiers.length > 0 ? <div className="flex flex-wrap items-center gap-x-3 gap-y-1">{qualifiers}</div> : undefined}
              />
            );
          })}
        </MobileCardList>
      ) : (
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            <colgroup>
              {/* Eight desktop columns summing to exactly 100 (Session ARP, Q-4), each MEASURED in real
                  Chromium with the office's own fonts on the 964px table a 1280px desktop renders with
                  the sidebar open (content + the 24px px-3 padding): Quote 13 (reference 78px, and the
                  site line beneath wraps; at 11 it ran to four lines), Status 18 (widest pill "AWAITING
                  CUSTOMER" 145px; pinned by quotesTableLayout.test.ts, as Session AFT measured), Created
                  11 and Opened 11 (widest date "22 June 2026" 79px; they clipped at 10), Customer 12 (it
                  truncates by design, and carries the portfolio line beneath), Tier 11 (the COMPLETE pill
                  73px), Value 9 ("£2,190/yr" 56px) and Actions 15 (the widest routine primary, "Mark
                  lost", plus the "..." trigger: 112px on one line; only the rare "Review frequency"
                  wraps its trigger beneath it). The portfolio is a line under the customer rather than a
                  ninth column: as a column it squeezed Customer to about 8%, a handful of characters. */}
              <col style={{ width: "13%" }} />
              <col style={{ width: "18%" }} />
              <col className="max-md:hidden" style={{ width: "11%" }} />
              <col className="max-md:hidden" style={{ width: "11%" }} />
              <col style={{ width: "12%" }} />
              <col className="max-md:hidden" style={{ width: "11%" }} />
              <col className="max-md:hidden" style={{ width: "9%" }} />
              <col className="max-md:hidden" style={{ width: "15%" }} />
              {/* Mobile-only disclosure chevron: every row taps through to its Hub now. */}
              <col className="w-8 md:hidden" />
            </colgroup>
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                <SortableHeader label="Quote" colKey="ref" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Status" colKey="status" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Created" colKey="created" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Opened" colKey="opened" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Customer" colKey="customer" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Tier" colKey="tier" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Value" colKey="value" sort={sort} onToggle={sort.toggle} className="max-md:hidden" align="right" />
                <th className="cell-actions max-md:hidden px-3 py-2 text-right">Actions</th>
                <th className="w-8 cell-actions md:hidden" aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((q) => {
                const tier = shownTier(q);
                return (
                <tr
                  key={q.id}
                  className="group cursor-pointer border-t border-ele-purple-accent"
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest("a,button,input,select,textarea,label")) return;
                    // Session AQW (Q-1): the row opens the office's own Quote Hub now that one
                    // exists, not the customer-facing accept page (View quote, in the row's menu,
                    // is the dedicated action for that). Every quote has a hub, including a draft.
                    // A click inside the menu never reaches here: OverflowMenu stops it.
                    navigate(`/office/quotes/${encodeURIComponent(q.id)}`);
                  }}
                >
                    <td className="px-3 py-2 align-top">
                      <div className="font-heading text-ele-strong">{q.quoteRef}</div>
                      {quoteSiteLine(q) && (
                        // Item 11 (Session ACB): the site line WRAPS within the column instead of being
                        // clipped on one line by the fixed-layout table's inherited nowrap/overflow.
                        <div className="mt-0.5 whitespace-normal break-words text-xs text-ele-body" title={quoteSiteLine(q)}>
                          {quoteSiteLine(q)}
                        </div>
                      )}
                      {/* Session ARP (Q-4): a renewal quotation says so, so it is never mistaken for new
                          business (it is not revised in the calculator; its contract's page decides it). */}
                      {q.renewalOfContractId && (
                        <StatusPill word="Renewal" tone="grey" className="mt-1" title="This quote renews an existing contract" />
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <StatusPill word={quoteStatusLabel(q.status)} tone={quoteStatusTone(q.status)} />
                      {/* Session AAT: a lost quote shows its reason as a chip that filters the list
                          to that reason; the optional note is a tooltip. */}
                      {q.status === "lost" && q.lostReason && (
                        <button
                          type="button"
                          onClick={() => setReasonFilter({ id: q.lostReason!.id, label: q.lostReason!.label })}
                          title={q.lostNote ?? "Filter by this reason"}
                          className="mt-1 block text-left font-menu text-[0.65rem] tracking-wide text-ele-danger underline decoration-dotted hover:text-ele-strong"
                        >
                          {q.lostReason.label}
                        </button>
                      )}
                      {/* Session AAT: the validity window on a live/expired quote. */}
                      {q.validUntil && (q.status === "pending" || q.status === "sent" || q.status === "expired") && (
                        <span className={`mt-1 block font-menu text-[0.6rem] uppercase tracking-wide ${q.status === "expired" ? "text-ele-danger" : "text-ele-body opacity-70"}`}>
                          {q.status === "expired" ? "Expired" : "Valid to"} {formatDate(q.validUntil)}
                        </span>
                      )}
                      {/* Session AAO: an accepted quote whose customer asked to change the service
                          frequency is held until the office confirms it. Amber indicator; the row's
                          Review frequency control opens the confirm panel. */}
                      {q.frequencyChangePending && (
                        <span className="mt-1 block font-menu text-[0.65rem] uppercase tracking-wide text-amber-400">
                          Frequency change
                        </span>
                      )}
                      {/* Session AAQ: a manual price override bypassed the contract floor. The badge
                          shows the forced value; the reason + who set it are in the hover tooltip. */}
                      {q.override && (
                        <span
                          className="mt-1 block font-menu text-[0.65rem] uppercase tracking-wide text-amber-400"
                          title={overrideTooltip(q.override)}
                        >
                          Manual price {gbp(q.override.value)}
                        </span>
                      )}
                    </td>
                    <td className="max-md:hidden px-3 py-2 align-top">{formatDate(q.createdAt)}</td>
                    <td className="max-md:hidden px-3 py-2 align-top">
                      {openedText(q).opened ? (
                        <span className="text-ele-green">{openedText(q).text}</span>
                      ) : (
                        <span className="text-ele-body opacity-70">{openedText(q).text}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      {q.customer?.id ? (
                        <>
                          {/* SC9B: the symbol BEFORE the name, so a truncating cell cuts the name and never the symbol. */}
                          <AccountSymbol customerId={q.customer.id} />
                          <Link to={`/office/customers/${encodeURIComponent(q.customer.id)}`} className="text-ele-green underline-offset-2 hover:underline">
                            {q.customer.name}
                          </Link>
                        </>
                      ) : (
                        q.customer?.name ?? q.customerName ?? "None"
                      )}
                      {/* Session ARP (Q-4): the portfolio the quote is grouped into, if any, on its own
                          line under the customer it belongs to (a portfolio is one customer's quotes), so
                          sorting by Customer keeps a portfolio's quotes together. */}
                      {q.portfolio && (
                        <div className="mt-0.5 truncate text-xs text-ele-body" title={`Portfolio: ${q.portfolio.name}`}>
                          <span className="opacity-70">Portfolio: </span>
                          <Link
                            to={`/office/quote-portfolios/${encodeURIComponent(q.portfolio.id)}`}
                            className="underline-offset-2 hover:text-ele-strong hover:underline"
                          >
                            {q.portfolio.name}
                          </Link>
                        </div>
                      )}
                    </td>
                    {/* Session ARP (Q-4): the tier the customer ACCEPTED once there is one, else the one
                        the quote recommends (shownTier, which the sort and the mobile card read too). */}
                    <td className="max-md:hidden px-3 py-2 align-top">
                      {tier ? (
                        <>
                          <StatusPill word={TIER_FILTER_LABEL[tier.tier]} tone="purple" title={tier.accepted ? "Accepted tier" : "Recommended tier"} />
                          {tier.accepted && (
                            <span className="mt-1 block font-menu text-[0.6rem] uppercase tracking-wide text-ele-body opacity-70">
                              Accepted
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-ele-body opacity-70">-</span>
                      )}
                    </td>
                    <td className="max-md:hidden px-3 py-2 text-right align-top text-ele-strong">
                      {valueText(q)}
                    </td>
                    <td className="cell-actions max-md:hidden px-3 py-2 text-right align-top">
                      {renderRowActions(q)}
                    </td>
                    {/* Mobile-only disclosure chevron: brightens with the row's hover/focus (via
                        `group`). Every row now has a hub to tap through to (Session AQW). */}
                    <td className="cell-actions md:hidden pr-1 text-right">
                      <svg
                        aria-hidden
                        viewBox="0 0 16 16"
                        className="ml-auto h-4 w-4 fill-current text-ele-purple transition-colors group-hover:text-ele-green group-focus-within:text-ele-green"
                      >
                        <path d="M6 4l4 4-4 4z" />
                      </svg>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Session SC2C: 50 at a time; the total is the server's count of every quote matching the filters and search. */}
      {!loading && !listError && (
        <div className={page.refreshing ? "opacity-60" : undefined}>
          <Pager offset={page.offset} total={total} shown={filtered.length} pageSize={QUOTE_PAGE_SIZE} onOffset={page.setOffset} noun={["quote", "quotes"]} />
        </div>
      )}

      {/* One two-step CLICK confirm for both cases (Session AJ Item 5, no typing): a draft quote
          shows the standard delete copy; a signed quote (one accepted into a contract) adds an amber
          warning that its contract is archived and its Xero draft voided. confirmDelete passes the
          signed flag as `force`. */}
      {freqReviewTarget && (
        <FrequencyConfirmModal
          quoteId={freqReviewTarget.id}
          quoteRef={freqReviewTarget.ref}
          onClose={() => setFreqReviewTarget(null)}
          onConfirmed={(message) => {
            setFreqReviewTarget(null);
            actionOk(message);
            void load();
          }}
        />
      )}

      <ConfirmDeleteModal
        open={deleteTarget !== null}
        title={`Delete quote ${deleteTarget?.ref ?? ""}?`}
        // The forced delete's copy is shared with the quote's own page (quoteActions.ts), word for word.
        message={deleteTarget?.signed ? QUOTE_FORCED_DELETE_MESSAGE : DELETE_CONFIRM_MESSAGE}
        warning={deleteTarget?.signed ? QUOTE_FORCED_DELETE_WARNING : undefined}
        confirmLabel="Delete quote"
        busy={deleting}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => {
          if (deleting) return;
          setDeleteTarget(null);
          setDeleteError(null);
        }}
      />

      {/* Session AAT: pick a reason (and optional note) to mark a quote lost. */}
      {lostTarget && (
        <MarkQuoteLostModal
          quoteId={lostTarget.id}
          quoteRef={lostTarget.ref}
          onClose={() => setLostTarget(null)}
          onManageReasons={() => {
            setLostTarget(null);
            setManageReasonsOpen(true);
          }}
          onMarked={(ref) => {
            setLostTarget(null);
            actionOk(`Quote ${ref} marked lost.`);
            void load();
          }}
        />
      )}

      {/* Session AAT: manage the quote lost-reason lookup (add / deactivate / delete). */}
      {manageReasonsOpen && <ManageLostReasonsModal onClose={() => setManageReasonsOpen(false)} />}

      {/* Session AAT: the append-only version history for a quote (field-level diffs; admin can
          delete a single historic version, never the live quote). */}
      {historyTarget && (
        <QuoteVersionsModal
          quoteId={historyTarget.id}
          quoteRef={historyTarget.ref}
          isAdmin={isAdmin}
          onClose={() => setHistoryTarget(null)}
        />
      )}
    </div>
  );
}
